import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';
import TypeBadge from '../components/pokemon/TypeBadge';
import { pokemonTypes } from '../data/pokemonData';

function AllTypesPage() {
  const breadcrumbItems = [
    { name: 'All Types' }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "All Pokemon Types - Complete Type Guide",
    "description": "Explore all 18 Pokemon types with detailed information, Pokemon counts, and type effectiveness. Complete Pokemon type reference guide.",
    "url": "https://example.com/types",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Pokemon Stats Database",
      "url": "https://example.com"
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8"
    >
      <SEO
        title="All Pokemon Types - Complete Type Guide & Reference"
        description="Explore all 18 Pokemon types including Fire, Water, Grass, Electric, and more. Complete Pokemon type reference with Pokemon counts and detailed information."
        keywords="pokemon types, all pokemon types, fire type, water type, grass type, electric type, pokemon type guide"
        url="/types"
        schema={pageSchema}
      />

      <div className="container mx-auto px-4">
        <Breadcrumb items={breadcrumbItems} />

        {/* Header */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">All Pokemon Types</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Explore all 18 Pokemon types with detailed information and complete Pokemon listings for each type.
          </p>
        </motion.div>

        {/* Type Grid */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6 mb-12"
        >
          {pokemonTypes.map((type, index) => (
            <motion.div
              key={type.name}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden group"
            >
              <Link 
                to={`/type/${type.name}`}
                className="block p-6 text-center focus:ring-2 focus:ring-blue-500 rounded-lg"
              >
                <div className="mb-4">
                  <TypeBadge type={type.name} size="lg" clickable={false} />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors capitalize">
                  {type.name}
                </h3>
                
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {type.count}
                </div>
                <div className="text-sm text-gray-600">
                  Pokemon
                </div>
                
                <div className="mt-4 text-sm text-blue-600 group-hover:text-blue-700 transition-colors">
                  View All →
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Type Statistics */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-lg p-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Type Statistics</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">18</div>
              <div className="text-gray-600">Total Types</div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">
                {Math.max(...pokemonTypes.map(t => t.count))}
              </div>
              <div className="text-gray-600">Most Pokemon</div>
              <div className="text-sm text-gray-500 mt-1">
                ({pokemonTypes.find(t => t.count === Math.max(...pokemonTypes.map(t => t.count)))?.name} type)
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600 mb-2">
                {Math.min(...pokemonTypes.map(t => t.count))}
              </div>
              <div className="text-gray-600">Fewest Pokemon</div>
              <div className="text-sm text-gray-500 mt-1">
                ({pokemonTypes.find(t => t.count === Math.min(...pokemonTypes.map(t => t.count)))?.name} type)
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {Math.round(pokemonTypes.reduce((sum, t) => sum + t.count, 0) / pokemonTypes.length)}
              </div>
              <div className="text-gray-600">Average per Type</div>
            </div>
          </div>
        </motion.div>

        {/* Top Types */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Most Popular Types</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pokemonTypes
              .sort((a, b) => b.count - a.count)
              .slice(0, 6)
              .map((type, index) => (
                <motion.div
                  key={type.name}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="bg-white rounded-lg shadow-md p-6"
                >
                  <Link 
                    to={`/type/${type.name}`}
                    className="flex items-center space-x-4 hover:text-blue-600 transition-colors focus:ring-2 focus:ring-blue-500 rounded"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      index === 0 ? 'medal-gold' : index === 1 ? 'medal-silver' : index === 2 ? 'medal-bronze' : 'bg-gray-400'
                    }`}>
                      {index + 1}
                    </div>
                    
                    <div className="flex-1">
                      <TypeBadge type={type.name} size="md" clickable={false} />
                    </div>
                    
                    <div className="text-right">
                      <div className="text-xl font-bold text-gray-900">{type.count}</div>
                      <div className="text-sm text-gray-600">Pokemon</div>
                    </div>
                  </Link>
                </motion.div>
              ))}
          </div>
        </motion.div>

        {/* Type Effectiveness Note */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <h3 className="text-lg font-semibold text-blue-900 mb-3">About Pokemon Types</h3>
          <p className="text-blue-800 mb-4">
            Pokemon types determine strengths, weaknesses, and battle effectiveness. Each Pokemon can have one or two types, 
            affecting which moves are super effective, not very effective, or have no effect against them.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <strong className="text-blue-900">Single Type:</strong> Pokemon with only one type
            </div>
            <div>
              <strong className="text-blue-900">Dual Type:</strong> Pokemon with two types, combining strengths and weaknesses
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default AllTypesPage;